import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface LayoutMap {
  [id: string]: any;
}

interface ReportLayoutState {
  reportLayouts: LayoutMap;
  inventoryLayouts: LayoutMap;
  checklistLayouts: LayoutMap;
  trackerLayouts: LayoutMap;
}

const initialState: ReportLayoutState = {
  reportLayouts: {},
  inventoryLayouts: {},
  checklistLayouts: {},
  trackerLayouts: {},
};

// Helper to normalize an array → object keyed by _id or layoutId
const normalizeArray = (arr: any[], idKey: string = '_id'): LayoutMap =>
  arr.reduce((acc, item) => {
    const id = item[idKey] || item.layoutId;
    if (id) acc[id] = item;
    return acc;
  }, {} as LayoutMap);

const reportLayouts = createSlice({
  name: 'reportLayouts',
  initialState,
  reducers: {
    setReportLayouts: (state, action: PayloadAction<any[]>) => {
      state.reportLayouts = normalizeArray(action.payload, 'reportLayoutId');
    },
    setInventoryLayouts: (state, action: PayloadAction<any[]>) => {
      state.inventoryLayouts = normalizeArray(
        action.payload,
        'inventoryLayoutId'
      );
    },
    setChecklistLayouts: (state, action: PayloadAction<any[]>) => {
      state.checklistLayouts = normalizeArray(
        action.payload,
        'checklistLayoutId'
      );
    },
    setTrackerLayouts: (state, action: PayloadAction<any[]>) => {
      state.trackerLayouts = normalizeArray(action.payload, 'trackerLayoutId');
    },
    // Optional: update single entry
    upsertReportLayout: (state, action: PayloadAction<any>) => {
      const layout = action.payload;
      const id = layout.reportLayoutId || layout._id;
      if (id) state.reportLayouts[id] = layout;
    },
    deleteReportLayout: (state, action: PayloadAction<string>) => {
      delete state.reportLayouts[action.payload];
    },
  },
});

export const {
  setChecklistLayouts,
  setTrackerLayouts,
  setReportLayouts,
  setInventoryLayouts,
  upsertReportLayout,
  deleteReportLayout,
} = reportLayouts.actions;

export default reportLayouts.reducer;

// ---------- Selectors ----------
export const selectReportLayoutById = (s: any, id: string) =>
  s?.reportLayouts?.reportLayouts?.[id] ?? null;

export const selectInventoryLayoutById = (s: any, id: string) =>
  s?.reportLayouts?.inventoryLayouts?.[id] ?? null;

export const selectChecklistLayoutById = (s: any, id: string) =>
  s?.reportLayouts?.checklistLayouts?.[id] ?? null;

export const selectTrackerLayoutById = (s: any, id: string) =>
  s?.reportLayouts?.trackerLayouts?.[id] ?? null;
